import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  KeyIcon,
  EyeIcon,
  EyeSlashIcon 
} from '@heroicons/react/24/outline';

const StaffLogin: React.FC = () => {
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const { staffLogin, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Handle navigation if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await staffLogin(accessKey.trim().toUpperCase());
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Staff login error:', err);
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.detail || 
                      'Invalid access key. Please contact your studio owner.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 text-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100 flex flex-col">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600 py-8 sm:py-10 lg:py-12 flex-shrink-0">
        <div className="mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mb-3 sm:mb-4 flex justify-center">
            <img 
              src="/SOMS_logo.png" 
              alt="SOMS Logo" 
              className="h-12 sm:h-16 w-12 sm:w-16 rounded-2xl object-contain drop-shadow-lg" 
            />
          </div>
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg px-2">
            Team Portal
          </h1>
          
          <p className="mt-2 sm:mt-3 text-xs sm:text-sm font-medium text-purple-50 drop-shadow-md max-w-2xl mx-auto px-2">
            Access your studio workspace with your unique access key
          </p>
        </div>
      </div>

      {/* Login Card Section */}
      <div className="relative mx-auto max-w-md w-full px-4 py-8 sm:py-12 sm:px-6 lg:px-8 flex-grow flex flex-col justify-center">
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-white shadow-xl sm:shadow-2xl ring-1 ring-black/5 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
              <KeyIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Team Sign In</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Enter your access key provided by your studio owner
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Access Key Field */}
            <div>
              <label htmlFor="accessKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Access Key
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="accessKey"
                  type={showKey ? 'text' : 'password'}
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value.toUpperCase())}
                  className="block w-full rounded-xl border border-gray-200 bg-gray-50 py-3 pl-12 pr-12 text-sm font-mono uppercase tracking-widest focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white transition-all"
                  placeholder="XXXX-XXXX-XXXX"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600"
                >
                  {showKey ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Your access key was provided by your studio owner. Contact them if you've forgotten it.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !accessKey.trim()}
              className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In to Studio'
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-gray-500">
              Lost your access key?{' '}
              <span className="text-purple-600 font-medium">Contact your studio owner</span>
            </p>
            <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
              <p className="text-sm text-gray-500">
                Are you a studio owner?{' '}
                <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
